import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { planApi } from '@src/apis/admin';
import type { CurrentApplicationPlanResponseModel } from '@uniquegood/realworld-admin-interface';

type CurrentApplicationPlanQueryKey = readonly ['current-application-plan', string];

export interface UseCurrentApplicationPlanOptions {
    /**
     * React Query 옵션을 덮어씁니다.
     * `queryKey`와 `queryFn`은 내부에서 관리하므로 제외했습니다.
     */
    queryOptions?: Omit<
        UseQueryOptions<CurrentApplicationPlanResponseModel | null, Error, CurrentApplicationPlanResponseModel | null, CurrentApplicationPlanQueryKey>,
        'queryKey' | 'queryFn'
    >;
}

/**
 * 현재 사용중인 요금제 조회 훅
 *
 * @description
 * - 채널 ID를 받아 현재 사용중인 요금제를 조회합니다.
 * - PlanApi.getApplicationPlan을 사용하여 실제 API를 호출합니다.
 * - x-rwd-api-version은 '1.0'을 사용합니다.
 */
export const useCurrentApplicationPlan = (
    channelId: string,
    options: UseCurrentApplicationPlanOptions = {}
): UseQueryResult<CurrentApplicationPlanResponseModel | null> => {
    const { queryOptions } = options;
    const { staleTime, ...restQueryOptions } = queryOptions ?? {};

    const queryKey = useMemo<CurrentApplicationPlanQueryKey>(
        () => ['current-application-plan', channelId] as const,
        [channelId]
    );

    return useQuery<CurrentApplicationPlanResponseModel | null, Error, CurrentApplicationPlanResponseModel | null, CurrentApplicationPlanQueryKey>({
        queryKey,
        queryFn: async () => {
            const { data } = await planApi.getApplicationPlan(
                channelId,
                '1.0' // x-rwd-api-version
            );
            
            // API 응답 타입: CurrentApplicationPlanResponseModelApiSuccessResponseModel
            // data.data가 CurrentApplicationPlanResponseModel
            const response: CurrentApplicationPlanResponseModel = data.data;
            
            // planType이 없으면 null 반환 (무료 요금제)
            if (!response?.planType) {
                return null;
            }
            
            return response;
        },
        enabled: !!channelId,
        staleTime: staleTime ?? 30 * 1000,
        refetchOnMount: 'always',
        ...restQueryOptions,
    });
};



